// VenueOnboardingAdmin.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Staff workspace for venue onboarding.
// Route: /venue-onboarding
// Auth: binko_token JWT — staff phone must be in onboarding_staff or ADMIN_PHONES
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, Button, TextField, Chip,
  Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, InputLabel, FormControl,
  Tabs, Tab, Alert, Divider, IconButton, Snackbar,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { C } from '../theme';

const API = process.env.REACT_APP_API_URL || 'https://api.binko.me';

function authHeaders() {
  const token = localStorage.getItem('binko_token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

// ── Constants (mirrors backend) ───────────────────────────────────────────────
const BUSINESS_TYPES = ['bar','restaurant','cafe','lounge','club','hotel','other'];
const AUTHORITY_TYPES = ['owner','partner','director','general_manager','venue_manager','marketing_manager','other'];
const COMMERCIAL_OPTS = ['yes','no','requires_owner_approval'];

// ── Colour helpers ────────────────────────────────────────────────────────────
function statusColor(s) {
  if (s === 'verified')             return C.success;
  if (s === 'pending_verification') return C.orange;
  if (s === 'rejected')             return '#ff4560';
  if (s === 'inactive')             return C.textDisabled;
  return C.textMuted;
}

// ── Shared input sx ───────────────────────────────────────────────────────────
const inputSx = {
  '& .MuiOutlinedInput-root': { bgcolor: C.bgElevated, color: C.textPrimary },
  '& .MuiInputLabel-root': { color: C.textMuted },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
  '& .MuiSelect-select': { color: C.textPrimary },
};

// ─────────────────────────────────────────────────────────────────────────────
// DetailDialog
// ─────────────────────────────────────────────────────────────────────────────
function DetailDialog({ appId, staff, onClose, onRefresh }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState('');
  const [busy, setBusy]         = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject]     = useState(false);
  const [snack, setSnack]       = useState('');

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/onboarding/applications/${appId}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setErr('Failed to load application.'); setLoading(false); });
  }, [appId]);

  useEffect(() => { load(); }, [load]);

  async function act(endpoint, body = {}) {
    setBusy(endpoint);
    try {
      const r = await fetch(`${API}/api/onboarding/applications/${appId}/${endpoint}`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Action failed');
      setSnack('Done \u2713');
      load();
      if (onRefresh) onRefresh();
    } catch (e) {
      setSnack(`Error: ${e.message}`);
    } finally {
      setBusy('');
    }
  }

  async function sendContactOtp(role) {
    setBusy(`otp-${role}`);
    try {
      const r = await fetch(`${API}/api/onboarding/applications/${appId}/contacts/${role}/send-otp`, {
        method: 'POST', headers: authHeaders(),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      setSnack(`OTP sent to ${role} contact`);
    } catch (e) {
      setSnack(`Error: ${e.message}`);
    } finally {
      setBusy('');
    }
  }

  async function resendLink() {
    setBusy('resend');
    try {
      const r = await fetch(`${API}/api/onboarding/applications/${appId}/send-verification`, {
        method: 'POST', headers: authHeaders(),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      setSnack('Link generated');
      load();
    } catch (e) {
      setSnack(`Error: ${e.message}`);
    } finally {
      setBusy('');
    }
  }

  function copy(text) {
    navigator.clipboard.writeText(text).then(() => setSnack('Copied!'));
  }

  const isSuper = staff?.role === 'super';
  const app = data?.application;

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}
      PaperProps={{ sx: { bgcolor: C.bgCard, border: `1px solid ${C.border}` } }}>
      <DialogTitle sx={{ color: C.textPrimary, borderBottom: `1px solid ${C.border}`, pb: 1.5 }}>
        {app ? app.venue_name : 'Loading\u2026'}
        {app && (
          <Chip label={app.status} size="small"
            sx={{ ml: 1.5, bgcolor: 'transparent', border: `1px solid ${statusColor(app.status)}`,
                  color: statusColor(app.status), fontSize: '0.72rem' }} />
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress sx={{ color: C.teal }} /></Box>}
        {err && <Alert severity="error">{err}</Alert>}

        {app && (
          <>
            <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', mb: 1 }}>
              Venue Details
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
              {[
                ['Business Type', app.business_type],
                ['Legal Name', app.legal_business_name],
                ['Address', app.address],
                ['City', `${app.city}${app.state ? ', ' + app.state : ''}${app.pincode ? ' \u2014 ' + app.pincode : ''}`],
                ['GST', app.gst_number || '\u2014'],
                ['PAN', app.pan_number || '\u2014'],
                ['Website', app.website || '\u2014'],
                ['Instagram', app.instagram || '\u2014'],
              ].map(([label, val]) => (
                <Box key={label}>
                  <Typography sx={{ color: C.textMuted, fontSize: '0.72rem' }}>{label}</Typography>
                  <Typography sx={{ color: C.textPrimary, fontSize: '0.88rem' }}>{val || '\u2014'}</Typography>
                </Box>
              ))}
            </Box>

            <Divider sx={{ borderColor: C.border, my: 1.5 }} />

            <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', mb: 1 }}>
              Contacts
            </Typography>
            {(data.contacts || []).map(c => (
              <Box key={c.id} sx={{ bgcolor: C.bgElevated, borderRadius: 1, p: 1.5, mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography sx={{ color: C.textPrimary, fontWeight: 600, fontSize: '0.9rem' }}>
                      {c.name}
                      <Chip label={c.role} size="small"
                        sx={{ ml: 1, bgcolor: C.bgCard, color: C.textMuted, fontSize: '0.68rem' }} />
                      {c.mobile_verified_at && (
                        <Chip label="\u2713 verified" size="small"
                          sx={{ ml: 0.5, bgcolor: 'transparent', border: `1px solid ${C.success}`, color: C.success, fontSize: '0.68rem' }} />
                      )}
                    </Typography>
                    <Typography sx={{ color: C.textMuted, fontSize: '0.8rem' }}>
                      {c.mobile}{c.designation ? ` \u00b7 ${c.designation}` : ''}{c.email ? ` \u00b7 ${c.email}` : ''}
                    </Typography>
                  </Box>
                  <Button size="small" variant="outlined"
                    disabled={busy === `otp-${c.role}`}
                    onClick={() => sendContactOtp(c.role)}
                    sx={{ borderColor: C.teal, color: C.teal, fontSize: '0.75rem' }}>
                    {busy === `otp-${c.role}` ? <CircularProgress size={14} sx={{ color: C.teal }} /> : 'Send OTP'}
                  </Button>
                </Box>
              </Box>
            ))}

            <Divider sx={{ borderColor: C.border, my: 1.5 }} />

            {(app.verify_url || data.verify_url) && (
              <>
                <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', mb: 1 }}>
                  Verification Link
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                  <TextField
                    value={app.verify_url || data.verify_url || ''}
                    fullWidth size="small" InputProps={{ readOnly: true }}
                    sx={{ ...inputSx, '& input': { fontSize: '0.78rem' } }}
                  />
                  <IconButton onClick={() => copy(app.verify_url || data.verify_url || '')}
                    sx={{ color: C.teal }}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </>
            )}

            {(data.documents || []).length > 0 && (
              <>
                <Divider sx={{ borderColor: C.border, my: 1.5 }} />
                <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', mb: 1 }}>
                  Documents
                </Typography>
                {data.documents.map(doc => (
                  <Box key={doc.id} sx={{ display: 'flex', justifyContent: 'space-between',
                    bgcolor: C.bgElevated, borderRadius: 1, p: 1.2, mb: 0.8 }}>
                    <Typography sx={{ color: C.textPrimary, fontSize: '0.85rem' }}>{doc.doc_type}</Typography>
                    <Typography sx={{ color: C.textMuted, fontSize: '0.78rem' }}>{doc.doc_number || '\u2014'}</Typography>
                  </Box>
                ))}
              </>
            )}

            {(data.audit || []).length > 0 && (
              <>
                <Divider sx={{ borderColor: C.border, my: 1.5 }} />
                <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', mb: 1 }}>
                  Audit Trail
                </Typography>
                {data.audit.map(ev => (
                  <Box key={ev.id} sx={{ display: 'flex', justifyContent: 'space-between',
                    borderBottom: `1px solid ${C.border}`, py: 0.8 }}>
                    <Box>
                      <Typography sx={{ color: C.textPrimary, fontSize: '0.82rem' }}>{ev.action_type}</Typography>
                      <Typography sx={{ color: C.textMuted, fontSize: '0.72rem' }}>
                        {ev.actor_type}{ev.actor_id ? ` \u00b7 ${ev.actor_id}` : ''}
                      </Typography>
                    </Box>
                    <Typography sx={{ color: C.textMuted, fontSize: '0.72rem', whiteSpace: 'nowrap', ml: 2 }}>
                      {new Date(ev.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                    </Typography>
                  </Box>
                ))}
              </>
            )}

            {showReject && (
              <Box sx={{ mt: 2, bgcolor: C.bgElevated, borderRadius: 1, p: 2 }}>
                <Typography sx={{ color: C.textPrimary, mb: 1, fontSize: '0.88rem' }}>Reason for rejection:</Typography>
                <TextField
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  fullWidth multiline rows={2} placeholder="Required"
                  sx={inputSx}
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                  <Button size="small" variant="contained"
                    disabled={!rejectReason.trim() || busy === 'reject'}
                    onClick={() => act('reject', { reason: rejectReason })}
                    sx={{ bgcolor: '#c0392b', color: C.textPrimary }}>
                    Confirm Reject
                  </Button>
                  <Button size="small" onClick={() => setShowReject(false)} sx={{ color: C.textMuted }}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${C.border}`, px: 2, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
        {app && !['verified','rejected','inactive'].includes(app.status) && (
          <Button size="small" variant="outlined" disabled={!!busy}
            onClick={resendLink}
            sx={{ borderColor: C.teal, color: C.teal, fontSize: '0.78rem' }}>
            {busy === 'resend' ? <CircularProgress size={14} sx={{ color: C.teal }} /> : 'Resend Link'}
          </Button>
        )}
        {isSuper && app && app.status === 'pending_verification' && (
          <Button size="small" variant="outlined" disabled={!!busy}
            onClick={() => act('verify', {})}
            sx={{ borderColor: C.success, color: C.success, fontSize: '0.78rem' }}>
            {busy === 'verify' ? <CircularProgress size={14} sx={{ color: C.success }} /> : 'Manual Verify'}
          </Button>
        )}
        {isSuper && app && !['rejected','inactive'].includes(app.status) && (
          <Button size="small" variant="outlined" disabled={!!busy}
            onClick={() => setShowReject(v => !v)}
            sx={{ borderColor: '#c0392b', color: '#c0392b', fontSize: '0.78rem' }}>
            Reject
          </Button>
        )}
        {isSuper && app && app.status === 'verified' && (
          <Button size="small" variant="outlined" disabled={!!busy}
            onClick={() => act('deactivate', {})}
            sx={{ borderColor: C.orange, color: C.orange, fontSize: '0.78rem' }}>
            {busy === 'deactivate' ? <CircularProgress size={14} sx={{ color: C.orange }} /> : 'Deactivate'}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ color: C.textMuted }}>Close</Button>
      </DialogActions>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')}
        message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// NewApplicationForm
// ─────────────────────────────────────────────────────────────────────────────
function NewApplicationForm({ onSuccess }) {
  const emptyApp = {
    venue_name: '', legal_business_name: '', business_type: '', address: '',
    google_maps_link: '', city: '', state: '', pincode: '', website: '',
    instagram: '', authority_type: '', can_approve_commercials: '',
    gst_number: '', pan_number: '',
  };
  const emptyContact = { name: '', designation: '', mobile: '', email: '' };

  const [app, setApp]         = useState({ ...emptyApp });
  const [primary, setPrimary] = useState({ ...emptyContact });
  const [owner, setOwner]     = useState({ ...emptyContact });
  const [submitMode, setSubmitMode] = useState('submit');
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState('');
  const [verifyUrl, setVerifyUrl] = useState('');
  const [snack, setSnack]     = useState('');

  function setA(k, v) { setApp(prev => ({ ...prev, [k]: v })); }
  function setP(k, v) { setPrimary(prev => ({ ...prev, [k]: v })); }
  function setO(k, v) { setOwner(prev => ({ ...prev, [k]: v })); }

  function copy(text) {
    navigator.clipboard.writeText(text).then(() => setSnack('Copied!'));
  }

  async function submit(mode) {
    setErr('');
    setSubmitMode(mode);
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/onboarding/applications`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ mode, application: app, primary_contact: primary, owner_contact: owner }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to create');
      if (d.verify_url) setVerifyUrl(d.verify_url);
      onSuccess(d.application_id);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (verifyUrl) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="success" sx={{ mb: 2, bgcolor: '#0a2a10', color: '#88ee88', border: '1px solid #1a5a1a' }}>
          Application submitted! Share the verification link with the venue owner.
        </Alert>
        <Typography sx={{ color: C.textMuted, fontSize: '0.8rem', mb: 1 }}>Verification link (copy and share):</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField value={verifyUrl} fullWidth size="small" InputProps={{ readOnly: true }}
            sx={{ ...inputSx, '& input': { fontSize: '0.78rem' } }} />
          <IconButton onClick={() => copy(verifyUrl)} sx={{ color: C.teal }}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
        <Snackbar open={!!snack} autoHideDuration={2000} onClose={() => setSnack('')}
          message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
      </Box>
    );
  }

  const field = (label, key, setter, obj, opts = {}) => (
    <TextField key={key} label={label} value={obj[key]} size="small" fullWidth
      onChange={e => setter(key, e.target.value)} sx={{ ...inputSx, mb: 1.5 }} {...opts} />
  );

  const selectField = (label, key, setter, obj, choices) => (
    <FormControl key={key} fullWidth size="small" sx={{ mb: 1.5 }}>
      <InputLabel sx={{ color: C.textMuted }}>{label}</InputLabel>
      <Select value={obj[key]} label={label} onChange={e => setter(key, e.target.value)}
        sx={{ bgcolor: C.bgElevated, color: C.textPrimary, '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border } }}>
        {choices.map(c => <MenuItem key={c} value={c} sx={{ bgcolor: C.bgElevated, color: C.textPrimary }}>{c}</MenuItem>)}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ p: 2, maxWidth: 680 }}>
      <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', mb: 1.5 }}>Venue Info</Typography>
      {field('Venue Name *', 'venue_name', setA, app)}
      {field('Legal Business Name', 'legal_business_name', setA, app)}
      {selectField('Business Type *', 'business_type', setA, app, BUSINESS_TYPES)}
      {field('Address *', 'address', setA, app)}
      {field('Google Maps Link', 'google_maps_link', setA, app)}
      {field('City *', 'city', setA, app)}
      {field('State', 'state', setA, app)}
      {field('Pincode', 'pincode', setA, app)}
      {field('Website', 'website', setA, app)}
      {field('Instagram', 'instagram', setA, app)}

      <Divider sx={{ borderColor: C.border, my: 2 }} />
      <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', mb: 1.5 }}>Authority</Typography>
      {selectField('Authority Type', 'authority_type', setA, app, AUTHORITY_TYPES)}
      {selectField('Can Approve Commercials', 'can_approve_commercials', setA, app, COMMERCIAL_OPTS)}
      {field('GST Number', 'gst_number', setA, app)}
      {field('PAN Number', 'pan_number', setA, app)}

      <Divider sx={{ borderColor: C.border, my: 2 }} />
      <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', mb: 1.5 }}>Primary Contact</Typography>
      {field('Name *', 'name', setP, primary)}
      {field('Designation', 'designation', setP, primary)}
      {field('Mobile *', 'mobile', setP, primary, { inputProps: { inputMode: 'tel' } })}
      {field('Email', 'email', setP, primary)}

      <Divider sx={{ borderColor: C.border, my: 2 }} />
      <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', mb: 1.5 }}>Owner / Authorised Signatory</Typography>
      {field('Name *', 'name', setO, owner)}
      {field('Designation', 'designation', setO, owner)}
      {field('Mobile *', 'mobile', setO, owner, { inputProps: { inputMode: 'tel' } })}
      {field('Email', 'email', setO, owner)}

      {err && (
        <Alert severity="error" sx={{ mb: 2, bgcolor: '#2a1010', color: '#ff8a8a', border: '1px solid #5a1a1a' }}>{err}</Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
        <Button variant="outlined" disabled={busy} onClick={() => submit('draft')}
          sx={{ borderColor: C.textMuted, color: C.textMuted, flex: 1 }}>
          {busy && submitMode === 'draft' ? <CircularProgress size={18} /> : 'Save Draft'}
        </Button>
        <Button variant="contained" disabled={busy} onClick={() => submit('submit')}
          sx={{ bgcolor: C.teal, color: C.bgApp, fontWeight: 700, flex: 2, '&:hover': { bgcolor: C.tealFill } }}>
          {busy && submitMode === 'submit' ? <CircularProgress size={18} sx={{ color: C.bgApp }} /> : 'Submit & Generate Link'}
        </Button>
      </Box>
    </Box>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// StaffTab (super-only)
// ─────────────────────────────────────────────────────────────────────────────
function StaffTab() {
  const [staff, setStaff]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName]       = useState('');
  const [phone, setPhone]     = useState('');
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState('');
  const [snack, setSnack]     = useState('');

  function load() {
    setLoading(true);
    fetch(`${API}/api/onboarding/staff`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setStaff(d.staff || []); setLoading(false); })
      .catch(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  async function addStaff() {
    if (!name.trim() || !phone.trim()) { setErr('Name and phone are required.'); return; }
    setErr(''); setBusy(true);
    try {
      const r = await fetch(`${API}/api/onboarding/staff`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ name: name.trim(), phone: phone.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      setName(''); setPhone('');
      setSnack('Sub-admin added');
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  async function toggleActive(id, current) {
    try {
      const r = await fetch(`${API}/api/onboarding/staff/${id}`, {
        method: 'PATCH', headers: authHeaders(),
        body: JSON.stringify({ is_active: !current }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      setSnack(current ? 'Deactivated' : 'Activated');
      load();
    } catch (e) { setSnack(`Error: ${e.message}`); }
  }

  return (
    <Box sx={{ p: 2, maxWidth: 680 }}>
      <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', mb: 2 }}>
        Add Sub-Admin
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
        <TextField label="Name" value={name} onChange={e => setName(e.target.value)}
          size="small" sx={{ ...inputSx, minWidth: 180 }} />
        <TextField label="Phone (with country code)" value={phone} onChange={e => setPhone(e.target.value)}
          size="small" sx={{ ...inputSx, minWidth: 220 }} inputProps={{ inputMode: 'tel' }} />
        <Button variant="contained" disabled={busy} onClick={addStaff}
          sx={{ bgcolor: C.teal, color: C.bgApp, fontWeight: 700, '&:hover': { bgcolor: C.tealFill } }}>
          {busy ? <CircularProgress size={18} sx={{ color: C.bgApp }} /> : 'Add'}
        </Button>
      </Box>
      {err && <Alert severity="error" sx={{ mb: 2, bgcolor: '#2a1010', color: '#ff8a8a', border: '1px solid #5a1a1a', fontSize: '0.85rem' }}>{err}</Alert>}

      <Divider sx={{ borderColor: C.border, my: 2 }} />
      <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', mb: 1.5 }}>
        Current Staff
      </Typography>
      {loading && <CircularProgress size={20} sx={{ color: C.teal }} />}
      {!loading && staff.length === 0 && (
        <Typography sx={{ color: C.textMuted, fontSize: '0.88rem' }}>No sub-admins yet.</Typography>
      )}
      {staff.map(s => (
        <Box key={s.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          bgcolor: C.bgElevated, borderRadius: 1, p: 1.5, mb: 1 }}>
          <Box>
            <Typography sx={{ color: C.textPrimary, fontWeight: 600, fontSize: '0.9rem' }}>
              {s.name}
              <Chip label={s.is_active ? 'active' : 'inactive'} size="small"
                sx={{ ml: 1, bgcolor: 'transparent',
                  border: `1px solid ${s.is_active ? C.success : C.textDisabled}`,
                  color: s.is_active ? C.success : C.textDisabled, fontSize: '0.68rem' }} />
            </Typography>
            <Typography sx={{ color: C.textMuted, fontSize: '0.8rem' }}>{s.phone}</Typography>
          </Box>
          <Button size="small" variant="outlined"
            onClick={() => toggleActive(s.id, s.is_active)}
            sx={{ borderColor: s.is_active ? C.orange : C.teal,
                  color: s.is_active ? C.orange : C.teal, fontSize: '0.75rem' }}>
            {s.is_active ? 'Deactivate' : 'Activate'}
          </Button>
        </Box>
      ))}
      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')}
        message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
export default function VenueOnboardingAdmin() {
  const [authState, setAuthState] = useState('loading');
  const [staff, setStaff]         = useState(null);
  const [tab, setTab]             = useState(0);
  const [apps, setApps]           = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [filters, setFilters]     = useState({ search: '', status: '', business_type: '' });
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/onboarding/whoami`, { headers: authHeaders() })
      .then(r => {
        if (r.status === 401 || r.status === 403) { setAuthState('denied'); return null; }
        return r.json();
      })
      .then(d => { if (d) { setStaff(d.staff); setAuthState('ok'); } })
      .catch(() => setAuthState('denied'));
  }, []);

  const loadApps = useCallback(() => {
    setAppsLoading(true);
    const p = new URLSearchParams();
    if (filters.search)        p.set('search', filters.search);
    if (filters.status)        p.set('status', filters.status);
    if (filters.business_type) p.set('business_type', filters.business_type);
    fetch(`${API}/api/onboarding/applications?${p}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setApps(d.applications || []); setAppsLoading(false); })
      .catch(() => setAppsLoading(false));
  }, [filters]);

  useEffect(() => {
    if (authState === 'ok' && tab === 0) loadApps();
  }, [authState, tab, loadApps]);

  if (authState === 'loading') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: C.bgApp, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: C.teal }} />
      </Box>
    );
  }
  if (authState === 'denied') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: C.bgApp, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: '2rem', mb: 1 }}>\uD83D\uDD12</Typography>
          <Typography sx={{ color: C.textPrimary, fontWeight: 700, mb: 1 }}>Access Denied</Typography>
          <Typography sx={{ color: C.textMuted, fontSize: '0.9rem' }}>
            You need to be a Binko onboarding staff member to access this page.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: C.bgApp, color: C.textPrimary }}>
      <Box sx={{ bgcolor: C.bgCard, borderBottom: `1px solid ${C.border}`, px: 3, py: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: C.textPrimary }}>
            Binko \u2014 Venue Onboarding
          </Typography>
          <Typography sx={{ color: C.textMuted, fontSize: '0.78rem' }}>
            {staff?.name || staff?.phone} \u00b7 {staff?.role}
          </Typography>
        </Box>
        <Chip
          label={staff?.role === 'super' ? '\u2B50 Super Admin' : 'Sub Admin'}
          size="small"
          sx={{ bgcolor: staff?.role === 'super' ? C.yellow : C.bgElevated,
                color: staff?.role === 'super' ? C.yellowText : C.textMuted,
                fontWeight: 700, fontSize: '0.72rem' }}
        />
      </Box>

      <Box sx={{ bgcolor: C.bgCard, borderBottom: `1px solid ${C.border}` }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ '& .MuiTab-root': { color: C.textMuted, fontSize: '0.85rem' },
               '& .Mui-selected': { color: C.teal },
               '& .MuiTabs-indicator': { bgcolor: C.teal } }}>
          <Tab label="Applications" />
          <Tab label="New Application" />
          {staff?.role === 'super' && <Tab label="Staff" />}
        </Tabs>
      </Box>

      {tab === 0 && (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search venue name\u2026" size="small" value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              sx={{ ...inputSx, minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel sx={{ color: C.textMuted }}>Status</InputLabel>
              <Select value={filters.status} label="Status"
                onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                sx={{ bgcolor: C.bgElevated, color: C.textPrimary, '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border } }}>
                <MenuItem value="" sx={{ bgcolor: C.bgElevated, color: C.textMuted }}>All</MenuItem>
                {['draft','pending_verification','verified','rejected','inactive'].map(s => (
                  <MenuItem key={s} value={s} sx={{ bgcolor: C.bgElevated, color: C.textPrimary }}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel sx={{ color: C.textMuted }}>Type</InputLabel>
              <Select value={filters.business_type} label="Type"
                onChange={e => setFilters(f => ({ ...f, business_type: e.target.value }))}
                sx={{ bgcolor: C.bgElevated, color: C.textPrimary, '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border } }}>
                <MenuItem value="" sx={{ bgcolor: C.bgElevated, color: C.textMuted }}>All</MenuItem>
                {BUSINESS_TYPES.map(bt => (
                  <MenuItem key={bt} value={bt} sx={{ bgcolor: C.bgElevated, color: C.textPrimary }}>{bt}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" size="small" onClick={loadApps}
              sx={{ borderColor: C.teal, color: C.teal }}>
              {appsLoading ? <CircularProgress size={16} sx={{ color: C.teal }} /> : 'Search'}
            </Button>
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Venue','Type','City','Owner','Status','Created'].map(h => (
                    <TableCell key={h} sx={{ color: C.textMuted, fontSize: '0.75rem', borderColor: C.border,
                      bgcolor: C.bgCard, fontWeight: 700, textTransform: 'uppercase' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {apps.length === 0 && !appsLoading && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ color: C.textMuted, textAlign: 'center', py: 4, borderColor: C.border }}>
                      No applications found
                    </TableCell>
                  </TableRow>
                )}
                {apps.map(a => (
                  <TableRow key={a.id} hover onClick={() => setSelectedId(a.id)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: C.bgElevated }, '& td': { borderColor: C.border } }}>
                    <TableCell sx={{ color: C.textPrimary, fontSize: '0.85rem' }}>{a.venue_name}</TableCell>
                    <TableCell sx={{ color: C.textMuted, fontSize: '0.8rem' }}>{a.business_type}</TableCell>
                    <TableCell sx={{ color: C.textMuted, fontSize: '0.8rem' }}>{a.city}</TableCell>
                    <TableCell sx={{ color: C.textMuted, fontSize: '0.8rem' }}>{a.owner_name || '\u2014'}</TableCell>
                    <TableCell>
                      <Chip label={a.status} size="small"
                        sx={{ bgcolor: 'transparent', border: `1px solid ${statusColor(a.status)}`,
                          color: statusColor(a.status), fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell sx={{ color: C.textMuted, fontSize: '0.78rem' }}>
                      {new Date(a.created_at).toLocaleDateString('en-IN')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}

      {tab === 1 && (
        <NewApplicationForm
          onSuccess={(id) => { setSelectedId(id); setTab(0); loadApps(); }}
        />
      )}

      {tab === 2 && staff?.role === 'super' && <StaffTab />}

      {selectedId && (
        <DetailDialog
          appId={selectedId}
          staff={staff}
          onClose={() => setSelectedId(null)}
          onRefresh={loadApps}
        />
      )}
    </Box>
  );
}
